import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  required,
  SelectInput,
  TextInput,
  useNotify,
  useRecordContext,
  useTranslate,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { getJson, getUrl } from '../../providers/data'
import { useOrganizationSettings } from '../../utils/useOrganizationSettings'
import TagInsertInput from '../../components/inputs/TagInsertInput'
import {
  ActionType,
  AllowedActionTypesOut,
  NotificationConfigurationCreate,
  NotificatieType,
} from '../../types/apiRequests'
import {
  ACTIEVERZOEK_STATUS_OPTIONS,
  ACTION_TYPE_LABELS,
  CasetypeResponse,
  CatalogEntry,
  CustomFieldWithMapping,
  getMappingSourceOptions,
  getTemplateMeta,
  hasAttributeMagicString,
  HeaderRow,
  NOTIFICATION_TYPE_CHOICES,
  parseHeaderRows,
  TEMPLATE_PLACEHOLDER_HELP,
} from './notificationConfigConstants'

export { NOTIFICATION_TYPE_CHOICES, ACTION_TYPE_LABELS }

type NotificationConfigurationFormValues = Partial<NotificationConfigurationCreate> & {
  action_config?: Record<string, string>
}

interface ActionTypeChoice {
  id: ActionType
  name: string
}

const useAllowedActionTypeChoices = () => {
  const [choices, setChoices] = useState<ActionTypeChoice[]>([])

  useEffect(() => {
    const fetchAllowed = async () => {
      try {
        const { action_types: allowed } = await getJson<AllowedActionTypesOut>(
          `${getUrl()}/notification_configuration/allowed_action_types`
        )
        setChoices(
          // eslint-disable-next-line security/detect-object-injection
          allowed.map((id) => ({ id, name: ACTION_TYPE_LABELS[id] }))
        )
      } catch {
        setChoices(
          (Object.entries(ACTION_TYPE_LABELS) as Array<[ActionType, string]>)
            .map(([id, name]) => ({ id, name }))
        )
      }
    }
    void fetchAllowed()
  }, [])

  return choices
}

const NotificationConfigurationForm: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const record = useRecordContext()
  const isEdit = !!record?.id
  const { setValue } = useFormContext<NotificationConfigurationFormValues>()
  const notificationType =
    useWatch<NotificationConfigurationFormValues, 'notification_type'>({ name: 'notification_type' })
  const actionType =
    useWatch<NotificationConfigurationFormValues, 'action_type'>({ name: 'action_type' })
  const casetypeUuid =
    useWatch<NotificationConfigurationFormValues, 'casetype_uuid'>({ name: 'casetype_uuid' }) ?? ''
  const watchedActionConfig = useWatch<NotificationConfigurationFormValues, 'action_config'>({
    name: 'action_config',
  })
  const actionConfig: Record<string, string> = watchedActionConfig ?? {}

  const actionTypeChoices = useAllowedActionTypeChoices()
  const orgSettings = useOrganizationSettings()
  const orgUrl = orgSettings?.type_config?.zaaksysteem_url ?? ''
  const mappingSourceOptions = getMappingSourceOptions(notificationType)
  const { templateTags, templateCategories } = useMemo(
    () => getTemplateMeta(notificationType), [notificationType]
  )

  const [catalogResults, setCatalogResults] = useState<CatalogEntry[]>([])
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [selectedCasetype, setSelectedCasetype] = useState<CatalogEntry | null>(null)
  const [customFields, setCustomFields] = useState<CustomFieldWithMapping[]>([])

  const searchCatalog = useCallback(async (keyword: string) => {
    if (!orgUrl || keyword.length < 3) {
      setCatalogResults([])
      return
    }
    setCatalogLoading(true)
    try {
      const params = new URLSearchParams({ keyword })
      const results = await getJson<CatalogEntry[]>(
        `${getUrl()}/notification_configuration/search_catalog?${params.toString()}`
      )
      setCatalogResults(results)
    } catch {
      setCatalogResults([])
      notify('ra.notification.http_error', { type: 'warning' })
    } finally {
      setCatalogLoading(false)
    }
  }, [orgUrl])

  useEffect(() => {
    if (!casetypeUuid || !orgUrl || actionType !== ActionType.CreateCase) {
      setCustomFields([])
      return
    }
    const fetchCasetype = async () => {
      try {
        const response = await getJson<CasetypeResponse>(
          `${getUrl()}/notification_configuration/casetype/${casetypeUuid}`
        )
        const phases = response.data?.attributes?.phases ?? []
        if (phases.length > 0) {
          const fields = (phases[0].custom_fields ?? []).filter(hasAttributeMagicString)
          setCustomFields(fields)
        } else {
          setCustomFields([])
        }
      } catch {
        setCustomFields([])
        notify('ra.notification.http_error', { type: 'warning' })
      }
    }
    void fetchCasetype()
  }, [casetypeUuid, orgUrl, actionType])

  const handleConfigChange = (key: string, value: string) => {
    const newConfig = { ...actionConfig }
    if (value === '') {
      // eslint-disable-next-line security/detect-object-injection
      delete newConfig[key]
    } else {
      // eslint-disable-next-line security/detect-object-injection
      newConfig[key] = value
    }
    setValue('action_config', newConfig, { shouldDirty: true })
  }

  const [headerRows, setHeaderRows] = useState<HeaderRow[]>(() => parseHeaderRows(actionConfig.headers_json))

  useEffect(() => {
    setHeaderRows(parseHeaderRows(actionConfig.headers_json))
  }, [actionConfig.headers_json])

  const persistHeaders = (rows: HeaderRow[]) => {
    setHeaderRows(rows)
    const obj: Record<string, string> = {}
    for (const h of rows) {
      if (h.key.trim()) obj[h.key.trim()] = h.value
    }
    handleConfigChange('headers_json', Object.keys(obj).length > 0 ? JSON.stringify(obj) : '')
  }

  return (
    <>
      <SelectInput
        source="notification_type"
        label={translate('data.notificationType')}
        choices={NOTIFICATION_TYPE_CHOICES}
        fullWidth
        validate={required()}
      />
      {notificationType === NotificatieType.STATUS_ACTIEVERZOEK_GEWIJZIGD && (
        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel>{translate('data.actieverzoekStatuses')}</InputLabel>
          <Select
            multiple
            value={actionConfig.actieverzoek_statuses ? actionConfig.actieverzoek_statuses.split(',') : []}
            onChange={(e) => {
              const val = e.target.value as string[]
              handleConfigChange('actieverzoek_statuses', val.join(','))
            }}
            label={translate('data.actieverzoekStatuses')}
            renderValue={(selected) =>
              (selected )
                .map(s => ACTIEVERZOEK_STATUS_OPTIONS.find(o => o.value === s)?.label || s)
                .join(', ')
            }
          >
            {ACTIEVERZOEK_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={(actionConfig.actieverzoek_statuses || '').split(',').includes(opt.value)} />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <SelectInput
        source="action_type"
        label={translate('data.actionType')}
        choices={actionTypeChoices}
        fullWidth
        validate={required()}
        defaultValue={ActionType.CreateCase}
      />

      {actionType === ActionType.CreateCase && (
        <>
          <MuiTextField
            select
            fullWidth
            label={translate('data.caseTarget')}
            value={actionConfig.case_target || 'samenwerking'}
            onChange={(e) => handleConfigChange('case_target', e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="samenwerking">{translate('data.caseTargetSamenwerking')}</MenuItem>
            <MenuItem value="actieverzoek">{translate('data.caseTargetActieverzoek')}</MenuItem>
          </MuiTextField>
          {actionConfig.case_target === 'actieverzoek' && (
            <MuiTextField
              select
              fullWidth
              label={translate('data.relateToCaseSamenwerking')}
              value={actionConfig.relate_to_samenwerking || 'true'}
              onChange={(e) => handleConfigChange('relate_to_samenwerking', e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="true">{translate('ra.boolean.true')}</MenuItem>
              <MenuItem value="false">{translate('ra.boolean.false')}</MenuItem>
            </MuiTextField>
          )}
          <Box sx={{ mt: 2, width: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {translate('data.requestor')}
            </Typography>
            <MuiTextField
              select
              fullWidth
              label={translate('data.requestorType')}
              value={actionConfig.requestor_type || 'subject'}
              onChange={(e) => handleConfigChange('requestor_type', e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="subject">Subject</MenuItem>
              <MenuItem value="person">Person</MenuItem>
            </MuiTextField>
            <MuiTextField
              fullWidth
              label={translate('data.requestorReference')}
              value={actionConfig.requestor_reference || ''}
              onChange={(e) => handleConfigChange('requestor_reference', e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>
          {isEdit ? (
            <TextInput
              source="casetype_uuid"
              label={translate('data.casetypeUuid')}
              fullWidth
              disabled
            />
          ) : orgUrl && (
            <Autocomplete
              options={catalogResults}
              getOptionLabel={(option) => option.attributes?.name || option.id}
              loading={catalogLoading}
              value={selectedCasetype}
              onInputChange={(_e, value) => {
                void searchCatalog(value)
              }}
              onChange={(_e, value) => {
                setSelectedCasetype(value)
                setValue('casetype_uuid', value?.id || '', { shouldDirty: true })
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label={translate('data.casetypeUuid')}
                  fullWidth
                  margin="dense"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2 }}
            />
          )}
          {customFields.length > 0 && (
            <Box sx={{ mt: 2, width: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {translate('data.fieldMapping')}
              </Typography>
              {customFields.map((field) => (
                <Box
                  key={field.attribute_magic_string}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}
                >
                  <Typography variant="body2" sx={{ minWidth: 200 }}>
                    {field.title}
                  </Typography>
                  <MuiTextField
                    select
                    size="small"
                    value={actionConfig[field.attribute_magic_string] || ''}
                    onChange={(e) =>
                      handleConfigChange(field.attribute_magic_string, e.target.value)
                    }
                    sx={{ minWidth: 300 }}
                  >
                    {mappingSourceOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </MuiTextField>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      {actionType === ActionType.AddNote && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <MuiTextField
            select
            fullWidth
            label={translate('data.noteTarget')}
            value={actionConfig.note_target || 'samenwerking'}
            onChange={(e) => handleConfigChange('note_target', e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="samenwerking">{translate('data.noteTargetSamenwerking')}</MenuItem>
            <MenuItem value="actieverzoek">{translate('data.noteTargetActieverzoek')}</MenuItem>
          </MuiTextField>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {translate('data.noteContent')}
          </Typography>
          <TagInsertInput
            multiline
            minRows={4}
            value={actionConfig.content_template || ''}
            onChange={(v) => handleConfigChange('content_template', v)}
            tags={templateTags}
            categories={templateCategories}
            helperText={TEMPLATE_PLACEHOLDER_HELP}
          />
        </Box>
      )}

      {actionType === ActionType.SendMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {translate('data.messageConfig')}
          </Typography>
          <MuiTextField
            select
            fullWidth
            label={translate('data.messageTarget')}
            value={actionConfig.message_target || 'samenwerking'}
            onChange={(e) => handleConfigChange('message_target', e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="samenwerking">{translate('data.messageTargetSamenwerking')}</MenuItem>
            <MenuItem value="actieverzoek">{translate('data.messageTargetActieverzoek')}</MenuItem>
          </MuiTextField>
          <TagInsertInput
            label={translate('data.messageTo')}
            value={actionConfig.to_template || ''}
            onChange={(v) => handleConfigChange('to_template', v)}
            tags={templateTags}
            categories={templateCategories}
            helperText={TEMPLATE_PLACEHOLDER_HELP}
            sx={{ mb: 2 }}
          />
          <TagInsertInput
            label={translate('data.messageSubject')}
            value={actionConfig.subject_template || ''}
            onChange={(v) => handleConfigChange('subject_template', v)}
            tags={templateTags}
            categories={templateCategories}
            helperText={TEMPLATE_PLACEHOLDER_HELP}
            sx={{ mb: 2 }}
          />
          <TagInsertInput
            multiline
            minRows={4}
            label={translate('data.messageContent')}
            value={actionConfig.content_template || ''}
            onChange={(v) => handleConfigChange('content_template', v)}
            tags={templateTags}
            categories={templateCategories}
            helperText={TEMPLATE_PLACEHOLDER_HELP}
          />
        </Box>
      )}

      {actionType === ActionType.SyncDocument && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {translate('data.syncDocumentInfo')}
          </Typography>
        </Box>
      )}

      {actionType === ActionType.Archive && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {translate('data.archiveInfo')}
          </Typography>
        </Box>
      )}

      {actionType === ActionType.SendToUrl && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {translate('data.sendToUrlConfig')}
          </Typography>
          <TextInput
            source="url"
            label="URL"
            fullWidth
            helperText={TEMPLATE_PLACEHOLDER_HELP}
          />
          <MuiTextField
            select
            fullWidth
            label={translate('data.sendToUrlMethod')}
            value={actionConfig.http_method || 'POST'}
            onChange={(e) => handleConfigChange('http_method', e.target.value)}
            sx={{ mb: 2 }}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </MuiTextField>
          <TagInsertInput
            multiline
            minRows={6}
            label={translate('data.sendToUrlPayload')}
            value={actionConfig.payload_template || ''}
            onChange={(v) => handleConfigChange('payload_template', v)}
            tags={templateTags}
            categories={templateCategories}
            helperText={TEMPLATE_PLACEHOLDER_HELP}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translate('data.sendToUrlHeaders')}
          </Typography>
          {headerRows.map((header, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <MuiTextField
                size="small"
                label="Header"
                value={header.key}
                onChange={(e) => {
                  const updated = headerRows.map((h, i) => i === index ? { ...h, key: e.target.value } : h)
                  persistHeaders(updated)
                }}
                sx={{ flex: 1 }}
              />
              <MuiTextField
                size="small"
                label={translate('data.sendToUrlHeaderValue')}
                value={header.value}
                onChange={(e) => {
                  const updated = headerRows.map((h, i) => i === index ? { ...h, value: e.target.value } : h)
                  persistHeaders(updated)
                }}
                sx={{ flex: 2 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  const headers = headerRows.filter((_, i) => i !== index)
                  persistHeaders(headers)
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              persistHeaders([...headerRows, { key: '', value: '' }])
            }}
          >
            {translate('data.sendToUrlAddHeader')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default NotificationConfigurationForm
